import { type FC, type ReactNode } from "react";
import { Pencil } from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import { IconButton } from "@/shared/components/ui/icon-button";
import {
  Tooltip,
  TooltipContent,
  TooltipInfoTrigger,
  TooltipProvider,
} from "@/shared/components/ui/tooltip";
import { WHY_WE_ASK_TITLE } from "@/shared/constants/whyWeAsk";
import { cn } from "@/shared/lib/utils";

interface SummaryCardEditProps {
  isEditing: boolean;
  canEdit: boolean;
  isSaving: boolean;
  onEdit: () => void;
  onCancel: () => void;
  onDone: () => void;
}

interface SummaryCardProps {
  title: string;
  edit?: SummaryCardEditProps;
  children: ReactNode;
}

export const SummaryCard: FC<SummaryCardProps> = ({ title, edit, children }) => (
  <section className="w-full overflow-hidden rounded-md border border-primary bg-white">
    <header className="flex min-h-[73px] items-center gap-2 border-b border-primary px-6 py-4">
      <h2 className="flex-1 font-sans text-base font-semibold tracking-[-0.32px]">{title}</h2>
      {edit?.isEditing ? (
        <div className="flex gap-2">
          <Button variant="outline" onClick={edit.onCancel} disabled={edit.isSaving}>
            Cancel
          </Button>
          <Button variant="brand" onClick={edit.onDone} disabled={edit.isSaving}>
            Done
          </Button>
        </div>
      ) : (
        edit && (
          <IconButton label={`Edit ${title}`} onClick={edit.onEdit} disabled={!edit.canEdit}>
            <Pencil />
          </IconButton>
        )
      )}
    </header>
    <div className="flex flex-col divide-y divide-primary p-6">{children}</div>
  </section>
);

interface SummaryRowProps {
  label: string;
  children?: ReactNode;
  className?: string;
  tooltip?: string;
}

export const SummaryRow: FC<SummaryRowProps> = ({ label, children, className, tooltip }) => {
  const isEmpty =
    children === null || children === undefined || children === "" || children === false;
  return (
    <div
      className={cn(
        "flex gap-4 py-4 text-sm leading-[1.55] tracking-[-0.28px] first:pt-0 last:pb-0",
        className,
      )}
    >
      <p className="flex w-[200px] shrink-0 items-center gap-1 self-start text-secondary">
        {label}
        {tooltip && (
          <TooltipProvider>
            <Tooltip>
              <TooltipInfoTrigger aria-label={WHY_WE_ASK_TITLE} />
              <TooltipContent title={WHY_WE_ASK_TITLE}>{tooltip}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </p>
      <div className="min-w-0 flex-1 text-primary">
        {isEmpty ? <span className="text-tertiary">Not added</span> : children}
      </div>
    </div>
  );
};
